import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentConfig, Slot } from '@puckeditor/core'
import useEmblaCarousel from 'embla-carousel-react'
import { WithLayout, withLayout } from '../Layout'
import { ColorField } from '../../fields/ColorField'
import { Spacing, defaultSpacing, SpacingGrid } from '../../fields/SpacingField'
import * as styles from './Carousel.module.scss'

/**
 * A carousel that takes whatever an author puts in it (BR-5591).
 *
 * The slot is the Embla container, so every block dropped in becomes a slide and this
 * component never needs to know what a slide contains. That is what makes it reusable:
 * the Celebrando "Join Us Live" row is a set of Flex cards, but nothing here mentions
 * cards.
 *
 * Embla rather than a hand-rolled scroller because the theme already depends on it and
 * already uses it elsewhere. Drag, momentum, snapping and keyboard behaviour are the part
 * that is genuinely hard to get right.
 *
 * `slidesPerView` accepts fractions on purpose. The mobile design shows one card and a
 * sliver of the next, which is what tells a reader there is more to swipe; 1.2 or 1.5
 * expresses that, and a whole number cannot.
 */
export type CarouselProps = WithLayout<{
  items: Slot
  sizing: 'fraction' | 'auto'
  slidesPerView: number
  gap: number
  edgeSpacing: number
  innerPadding?: Spacing
  scrollBy: 'slide' | 'page'
  align: 'start' | 'center'
  loop: boolean
  navigation: 'auto' | 'always' | 'never'
  showDots: boolean
  showArrows: boolean
  navigationColor?: string
}>

/** A spacing value, or nothing when it is unset or zero — so the style stays absent. */
const cssOrZero = (value?: string) => {
  const v = String(value ?? '').trim()
  if (!v || v === '0') return undefined
  return /^-?\d+(\.\d+)?$/.test(v) ? `${v}px` : v
}

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M15 5 8 12l7 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="m9 5 7 7-7 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CarouselRender = ({
  items: Items,
  sizing,
  slidesPerView,
  gap,
  edgeSpacing,
  innerPadding,
  scrollBy,
  align,
  loop,
  navigation,
  showDots,
  showArrows,
  navigationColor,
  puck,
}: CarouselProps & { puck: { isEditing: boolean } }) => {
  const isEditing = Boolean(puck?.isEditing)
  const gapPx = Number(gap) >= 0 ? Number(gap) : 0
  const edgeSpacingPx = Number(edgeSpacing) >= 0 ? Number(edgeSpacing) : 0

  // Where each slide comes to rest, as a function so the two ends can be placed explicitly.
  //
  // The returned number is the pixel offset from the start of the viewport. The default
  // alignments give the same value to every slide; the ends need their own, because that is
  // the only place edge spacing can come from — see the note on containScroll below.
  const lastIndexRef = useRef(0)
  const containRef = useRef<'trimSnaps' | false | undefined>(undefined)
  const alignSlide = useCallback(
    (viewSize: number, snapSize: number, index: number) => {
      const base = align === 'center' ? (viewSize - snapSize) / 2 : 0
      if (!edgeSpacingPx) return base

      const lastIndex = lastIndexRef.current
      if (index === 0) return edgeSpacingPx
      if (index === lastIndex && lastIndex > 0) {
        return Math.max(base, viewSize - snapSize - edgeSpacingPx)
      }
      return base
    },
    [align, edgeSpacingPx],
  )

  // One options object, used both to create the carousel and to re-create it. `reInit`
  // REPLACES the options rather than merging into them, so handing it a partial object
  // silently resets everything absent from it — which is how the containment gating below
  // was undoing `slidesToScroll` and putting the dots back to one per slide.
  const emblaOptions = useMemo(
    () => ({
      align: edgeSpacingPx ? alignSlide : align,
      loop,
      // Off while editing so Embla never transforms the track out from under a drag.
      active: !isEditing,
      // `trimSnaps` exists to remove leading and trailing space — the docs are explicit
      // that it "overrides the align option for slides at the carousel's beginning and end
      // to eliminate leading/trailing space". So no padding or margin on the track can
      // survive it, which is what every earlier attempt at edge spacing ran into.
      //
      // With edge spacing, containment is off and the alignment function places every snap
      // instead. That is what the option warns about — "allows excessive scrolling around
      // empty space" — except there is no excess here, because the ends are positioned
      // deliberately rather than left at their default alignment.
      containScroll: loop ? undefined : edgeSpacingPx ? false : 'trimSnaps',
      // 'auto' groups whatever is visible at once into a single snap, so the dots count
      // pages rather than slides. Five slides four at a time is two dots — five dots for
      // two moves counts something the reader cannot do.
      slidesToScroll: (scrollBy === 'page' ? 'auto' : 1) as 'auto' | number,
    }),
    [align, alignSlide, edgeSpacingPx, isEditing, loop, scrollBy],
  )

  // Read inside the resize handler, which must not close over a stale object.
  const optionsRef = useRef(emblaOptions)
  optionsRef.current = emblaOptions

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions)

  const [selected, setSelected] = useState(0)
  const [snaps, setSnaps] = useState<number[]>([])
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)
  // Measured, not asked of Embla. With containment off it always believes it can scroll —
  // there are snaps either side whether or not anything is out of view — so its
  // `canScrollNext` stops being an answer to "is there more than fits".
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    if (!emblaApi) return undefined

    const sync = () => {
      lastIndexRef.current = emblaApi.scrollSnapList().length - 1
      setSelected(emblaApi.selectedScrollSnap())
      setSnaps(emblaApi.scrollSnapList())
      setCanPrev(emblaApi.canScrollPrev())
      setCanNext(emblaApi.canScrollNext())

      const track = emblaApi.containerNode()
      const view = emblaApi.rootNode()
      const spills = track.scrollWidth > view.clientWidth + 1
      setOverflows(spills)

      // Containment is only worth losing while there is something to scroll to. With
      // everything visible it is what keeps the track where the layout put it, so a
      // carousel that fits is not nudged sideways by the edge-spacing alignment.
      const wanted = loop ? undefined : edgeSpacingPx && spills ? false : 'trimSnaps'
      if (containRef.current !== wanted) {
        containRef.current = wanted
        emblaApi.reInit({ ...optionsRef.current, containScroll: wanted })
      }
    }

    sync()
    emblaApi.on('select', sync)
    emblaApi.on('resize', sync)
    // Slides are authored content that can change size after mount — an image finishing
    // loading is enough — and the snap list is wrong until it settles.
    emblaApi.on('reInit', sync)

    return () => {
      emblaApi.off('select', sync)
      emblaApi.off('resize', sync)
      emblaApi.off('reInit', sync)
    }
  }, [emblaApi, loop, edgeSpacingPx])

  // Re-measure when the props that decide slide geometry change — and do it after the
  // browser has laid the change out, not in the effect.
  //
  // Embla reads the DOM synchronously. Called straight from an effect it measures the
  // layout that is on its way out, and the wrong numbers stick: at a 420px viewport with a
  // 1058px track it settled on a 319px scroll limit instead of 638px, which is a carousel
  // whose last slides simply cannot be reached. Two frames is what it takes for style and
  // layout to have been recomputed.
  useEffect(() => {
    if (!emblaApi) return undefined
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => emblaApi.reInit())
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [emblaApi, sizing, slidesPerView, gap, edgeSpacing, align, loop, alignSlide])

  // Development-only handle on the carousel API, so its geometry can be measured from the
  // console instead of inferred from the DOM. Embla's own numbers — the scroll limit, the
  // snap list, the container rect it measured — are the only way to tell a layout problem
  // apart from a measurement one, and guessing between those two cost several wrong fixes.
  // Stripped from production builds.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined
    if (typeof window === 'undefined' || !emblaApi) return undefined
    window.__embla = emblaApi
    return () => {
      if (window.__embla === emblaApi) delete window.__embla
    }
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  )

  const perView = Number(slidesPerView) > 0 ? Number(slidesPerView) : 1

  // One slide's share of the viewport, with the gaps between the visible slides taken out
  // first. Fractional counts fall out of the same expression.
  const slideSize = `calc((100% - ${gapPx * (perView - 1)}px) / ${perView})`

  // `auto` shows the controls only when there is more than fits. A loop always qualifies:
  // it is always scrollable, however few slides it holds.
  const hasMore = loop ? canPrev || canNext : overflows
  const navVisible =
    navigation === 'always' || (navigation === 'auto' && hasMore && !isEditing)

  const autoWidth = sizing === 'auto'

  const cssVars = {
    '--carousel-gap': `${gapPx}px`,
    '--carousel-slide-size': slideSize,
    '--carousel-nav-color': navigationColor || 'currentColor',
  } as React.CSSProperties

  return (
    <div className={styles.root} style={cssVars}>
      <div
        // Padding on the viewport, which is inside the clip: `overflow: hidden` cuts at the
        // padding box, so anything a slide hangs outside itself — the calendar tab above the
        // Celebrando cards — renders in that space instead of being sliced off. The block's
        // own Padding sits on the wrapper and takes the clipping edge with it, so it moves
        // the problem rather than solving it.
        style={{
          paddingTop: cssOrZero(innerPadding?.top),
          paddingRight: cssOrZero(innerPadding?.right),
          paddingBottom: cssOrZero(innerPadding?.bottom),
          paddingLeft: cssOrZero(innerPadding?.left),
        }}
        className={[
          styles.viewport,
          autoWidth ? styles.autoWidth : '',
          isEditing ? styles.editing : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={isEditing ? undefined : emblaRef}
      >
        <Items
          className={`${styles.container} no-slot-padding`}
          // Embla's `align` decides where a slide comes to rest when there is somewhere to
          // scroll. With everything already visible it has nothing to do, and the row is
          // laid out by flex — which starts at the left unless told otherwise. Centring
          // only applies while the track fits: once it overflows, Embla owns the position
          // and a centred flex row would fight it.
          style={
            align === 'center' && !hasMore
              ? { justifyContent: 'center' }
              : undefined
          }
        />
      </div>

      {navVisible && (showDots || showArrows) && (
        <div className={styles.nav}>
          {showArrows && (
            <button
              type="button"
              className={styles.arrow}
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canPrev}
              aria-label="Previous"
            >
              <ChevronLeft />
            </button>
          )}

          {showDots && (
            <div className={styles.dots}>
              {snaps.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={styles.dot}
                  aria-current={index === selected}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => scrollTo(index)}
                />
              ))}
            </div>
          )}

          {showArrows && (
            <button
              type="button"
              className={styles.arrow}
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canNext}
              aria-label="Next"
            >
              <ChevronRight />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const CarouselInternal: ComponentConfig<CarouselProps> = {
  label: 'Carousel',
  fields: {
    items: { type: 'slot' },
    sizing: {
      type: 'radio',
      label: 'Slide Width',
      options: [
        { label: 'Fit a number per view', value: 'fraction' },
        { label: 'Size to content', value: 'auto' },
      ],
    },
    slidesPerView: {
      type: 'number',
      label: 'Slides Per View (decimals allowed, e.g. 1.5 to peek the next one)',
      min: 0.5,
    },
    gap: { type: 'number', label: 'Gap Between Slides (px)', min: 0 },
    edgeSpacing: {
      type: 'number',
      label: 'Space Before First and After Last Slide (px)',
      min: 0,
    },
    innerPadding: {
      type: 'custom',
      label: 'Inner Padding (room inside the clipped area, for anything a slide overhangs)',
      render: ({ value, onChange }) => (
        <SpacingGrid
          title="Inner Padding"
          value={typeof value === 'object' && value ? value : defaultSpacing}
          onChange={onChange}
        />
      ),
    },
    scrollBy: {
      type: 'radio',
      label: 'Move By',
      options: [
        { label: 'One slide', value: 'slide' },
        { label: 'A full page', value: 'page' },
      ],
    },
    align: {
      type: 'radio',
      label: 'Align Slides',
      options: [
        { label: 'Start', value: 'start' },
        { label: 'Center', value: 'center' },
      ],
    },
    loop: {
      type: 'radio',
      label: 'Loop',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navigation: {
      type: 'radio',
      label: 'Show Navigation',
      options: [
        { label: 'When needed', value: 'auto' },
        { label: 'Always', value: 'always' },
        { label: 'Never', value: 'never' },
      ],
    },
    showDots: {
      type: 'radio',
      label: 'Dots',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    showArrows: {
      type: 'radio',
      label: 'Arrows',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    navigationColor: {
      type: 'custom',
      label: 'Navigation Color',
      render: (props: React.ComponentProps<typeof ColorField>) => (
        <ColorField {...props} clearable />
      ),
    },
  },
  defaultProps: {
    items: [],
    sizing: 'fraction',
    slidesPerView: 3,
    gap: 20,
    edgeSpacing: 0,
    innerPadding: defaultSpacing,
    scrollBy: 'slide',
    align: 'start',
    loop: false,
    navigation: 'auto',
    showDots: true,
    showArrows: true,
    navigationColor: '',
    // Stretch, not the usual auto. The wrapper Layout is a flex item, and in a column flex
    // an item that is not stretched takes its width from its content — which here is the
    // whole track. It then grows past its parent and the overflow lands on the page. An
    // author can still change it; this only decides what a new carousel does.
    layout: {
      alignSelf: 'stretch',
    },
  },
  // Slides Per View means nothing when slides size to their content, and a field that
  // silently does nothing is worse than one that is absent.
  resolveFields: (data, { fields }) =>
    data.props?.sizing === 'auto'
      ? Object.fromEntries(
          Object.entries(fields).filter(([name]) => name !== 'slidesPerView'),
        )
      : fields,
  render: CarouselRender as ComponentConfig<CarouselProps>['render'],
}

export const PuckCarousel = withLayout(CarouselInternal)
